import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import clientService from '../../services/clientService';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/common/PageHeader';
import FilterCard from '../../components/common/FilterCard';
import { useI18n } from '../../i18n/I18nContext';
import './ClientList.css';

function ClientList() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const { t } = useI18n();

    useEffect(() => {
        const fetchClients = async () => {
            try {
                setLoading(true);
                const data = await clientService.getAllClients();
                setClients(data);
                setError(null);
            } catch (err) {
                setError(t('client.loadFailed'));
            } finally {
                setLoading(false);
            }
        };

        fetchClients();
    }, [t]);

    const handleRowClick = (id) => {
        navigate(`/clients/edit/${id}`);
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (window.confirm(t('client.confirmDelete'))) {
            try {
                await clientService.deleteClient(id);
                setClients(clients.filter(client => client.id !== id));
            } catch (err) {
                setError(t('client.deleteFailed'));
            }
        }
    };

    const filteredClients = clients.filter(client => {
        const query = search.toLowerCase();
        return (
            client.nom.toLowerCase().includes(query) ||
            client.prenom.toLowerCase().includes(query) ||
            (client.email && client.email.toLowerCase().includes(query)) ||
            (client.telephone && client.telephone.toLowerCase().includes(query)) ||
            (client.entreprise && client.entreprise.toLowerCase().includes(query))
        );
    });

    if (loading) {
        return (
            <DashboardLayout>
                <div className="client-list-container">
                    <div className="container">
                        <div className="loading-spinner text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">{t('common.loading')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="client-list-container">
                <div className="container">
                    <PageHeader
                        title={t('client.title')}
                        subtitle={t('client.subtitle')}
                        buttonText={t('client.addClient')}
                        buttonTo="/clients/create"
                    />

                    {error && (
                        <div className="alert alert-danger alert-custom" role="alert">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </div>
                    )}

                    <FilterCard>
                        <div className="row g-3 align-items-center">
                            <div className="col-lg-10">
                                <div className="input-group search-input-group">
                                    <span className="input-group-text">
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={t('missions.missionList.searchPlaceholder')}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <button
                                    className="btn btn-outline-secondary w-100 clear-btn"
                                    onClick={() => setSearch('')}
                                >
                                    <i className="bi bi-arrow-clockwise"></i>
                                </button>
                            </div>
                        </div>
                    </FilterCard>

                    <div className="c-table-card card mt-4 shadow-sm">
                        {filteredClients.length === 0 ? (
                            <div className="card-body">
                                <div className="empty-state text-center py-5">
                                    <div className="empty-state-icon mb-3">
                                        <i className="bi bi-people text-muted" style={{ fontSize: '3rem' }}></i>
                                    </div>
                                    <h3 className="empty-state-title">
                                        {clients.length === 0 ? t('client.emptyState.noClients') : t('client.emptyState.noResults')}
                                    </h3>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="c-table table-hover align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th>{t('client.nom')}</th>
                                            <th>{t('client.entreprise')}</th>
                                            <th>{t('client.telephone')}</th>
                                            <th>{t('client.email')}</th>
                                            <th className="text-end">{t('missions.missionList.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredClients.map((client) => (
                                            <tr
                                                key={client.id}
                                                onClick={() => handleRowClick(client.id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td>
                                                    <div className="fw-bold">{client.prenom} {client.nom}</div>
                                                </td>
                                                <td>{client.entreprise || '-'}</td>
                                                <td>{client.telephone}</td>
                                                <td>{client.email || '-'}</td>
                                                <td className="text-end">
                                                    <div className="action-buttons">
                                                        <button
                                                            onClick={(e) => handleDelete(e, client.id)}
                                                            className="btn-action btn-delete"
                                                            title={t('client.deleteClient')}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}

export default ClientList;
